<script setup lang="ts">
import { computed, ref } from 'vue';
import { UiCheckbox, UiInputField, UiText } from '@shopexperts/shared';
import type { SmartFilterOption, SmartFilterOptionGroup } from '../../composables/useExpertFacets';

const props = withDefaults(defineProps<{
  options?: SmartFilterOption[];
  groups?: SmartFilterOptionGroup[];
  searchable?: boolean;
  searchPlaceholder?: string;
  loading?: boolean;
}>(), {
  options: () => [],
  groups: () => [],
  searchable: false,
  searchPlaceholder: 'Search...',
  loading: false,
});

const model = defineModel<string[]>({ required: true });

const search = ref('');
const query = computed(() => search.value.trim().toLowerCase());

function matches(option: SmartFilterOption): boolean {
  if (!query.value) return true;
  return option.label.toLowerCase().includes(query.value) || option.value.toLowerCase().includes(query.value);
}

const visibleOptions = computed(() => props.options.filter(matches));

// A matching group heading keeps all its children; otherwise only matching
// children survive, so searching "email" still shows the parent category.
const visibleGroups = computed(() => props.groups
  .map((group) => {
    const groupMatches = matches(group);
    const options = groupMatches ? group.options : group.options.filter(matches);
    return { ...group, options, groupMatches };
  })
  .filter(group => group.groupMatches || group.options.length));

const hasAnyOptions = computed(() => props.options.length || props.groups.length);
</script>

<template>
  <div class="flex flex-col gap-3">
    <UiInputField
      v-if="props.searchable && hasAnyOptions"
      v-model="search"
      icon="search-md"
      :placeholder="props.searchPlaceholder"
      size="md"
    />

    <UiText v-if="props.loading" as="p" variant="caption-normal" class="text-se-gray-500" text="Loading options..." />
    <UiText
      v-else-if="!hasAnyOptions"
      as="p"
      variant="caption-normal"
      class="text-se-gray-500"
      text="No options available."
    />
    <UiText
      v-else-if="!visibleOptions.length && !visibleGroups.length"
      as="p"
      variant="caption-normal"
      class="text-se-gray-500"
      text="No options match your search."
    />

    <div v-else class="flex max-h-64 flex-col gap-2 overflow-y-auto pr-1">
      <template v-if="props.groups.length">
        <div v-for="group in visibleGroups" :key="group.value" class="flex flex-col gap-2">
          <UiCheckbox v-model="model" :value="group.value" :label="group.label" class="font-semibold" />
          <div v-if="group.options.length" class="ml-6 flex flex-col gap-2">
            <UiCheckbox
              v-for="option in group.options"
              :key="option.value"
              v-model="model"
              :value="option.value"
              :label="option.label"
            />
          </div>
        </div>
      </template>
      <template v-else>
        <UiCheckbox v-for="option in visibleOptions" :key="option.value" v-model="model" :value="option.value">
          <span class="row-start-1 col-start-2 flex min-w-0 items-center gap-2">
            <img v-if="option.logo" :src="option.logo" alt="" class="h-5 w-5 shrink-0 rounded object-contain" loading="lazy" />
            <UiText as="span" variant="p-small" class="min-w-0 truncate leading-tight" :text="option.label" />
          </span>
        </UiCheckbox>
      </template>
    </div>
  </div>
</template>
